<script setup lang="ts">
import { BarChart, LineChart } from 'echarts/charts'
import { GridComponent, TooltipComponent } from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'
import { ofetch } from 'ofetch'
import { onMounted, onUnmounted, ref, shallowRef } from 'vue'

import * as echarts from 'echarts/core'

const props = withDefaults(defineProps<{
  apiBase?: string
  code?: string
  refreshInterval?: number
}>(), {
  apiBase: '/api/market',
  code: '000001.SH',
  refreshInterval: 60_000,
})

echarts.use([GridComponent, TooltipComponent, LineChart, BarChart, CanvasRenderer])

interface MinuteData {
  timestamp: string
  open: number
  close: number
  volume: number
  change_pct: number
}

const chartRef = ref<HTMLDivElement>()
const chart = shallowRef<echarts.ECharts>()
const latestPct = ref(0)
const latestPrice = ref(0)
const loading = ref(true)
const error = ref(false)

let timer: ReturnType<typeof setInterval> | undefined
let resizeObserver: ResizeObserver | undefined

function buildOption(data: MinuteData[]) {
  if (!data || data.length === 0)
    return null

  const times = data.map(d => d.timestamp.slice(11, 16))
  const closes = data.map(d => d.close)
  const volumes = data.map(d => d.volume)

  // Use first bar's open as the reference price (proxy for previous close)
  const prevClose = data[0].open
  const avgPrices = closes.map((_, i) => {
    const sum = closes.slice(0, i + 1).reduce((a, b) => a + b, 0)
    return +(sum / (i + 1)).toFixed(2)
  })

  const minPrice = Math.min(...closes, prevClose) * 0.999
  const maxPrice = Math.max(...closes, prevClose) * 1.001

  // Calculate change percentage from prevClose
  const lastClose = data[data.length - 1].close
  latestPct.value = +((lastClose - prevClose) / prevClose * 100).toFixed(2)
  latestPrice.value = lastClose

  const isUp = latestPct.value >= 0

  return {
    animation: false,
    backgroundColor: 'transparent',
    grid: [
      { left: 50, right: 12, top: 24, bottom: '32%' },
      { left: 50, right: 12, top: '74%', bottom: 20 },
    ],
    tooltip: {
      trigger: 'axis' as const,
      backgroundColor: 'rgba(0,0,0,0.7)',
      borderColor: 'rgba(255,255,255,0.08)',
      textStyle: { color: '#eee', fontSize: 11 },
      formatter(params: Array<{ seriesName: string, value: number, axisValue: string }>) {
        const p = params[0]
        if (!p)
          return ''
        const pct = ((p.value - prevClose) / prevClose * 100).toFixed(2)
        return `${p.axisValue}<br/>价格: ${p.value}<br/>涨跌: ${pct}%`
      },
    },
    xAxis: [
      {
        type: 'category' as const,
        data: times,
        gridIndex: 0,
        axisLabel: { show: false },
        axisTick: { show: false },
        axisLine: { lineStyle: { color: 'rgba(255,255,255,0.1)' } },
        splitLine: { show: false },
      },
      {
        type: 'category' as const,
        data: times,
        gridIndex: 1,
        axisLabel: { fontSize: 10, color: 'rgba(255,255,255,0.35)' },
        axisTick: { show: false },
        axisLine: { lineStyle: { color: 'rgba(255,255,255,0.1)' } },
        splitLine: { show: false },
      },
    ],
    yAxis: [
      {
        type: 'value' as const,
        gridIndex: 0,
        min: minPrice,
        max: maxPrice,
        splitNumber: 4,
        axisLabel: { fontSize: 10, color: 'rgba(255,255,255,0.35)' },
        splitLine: { lineStyle: { color: 'rgba(255,255,255,0.05)' } },
      },
      {
        type: 'value' as const,
        gridIndex: 1,
        splitNumber: 2,
        axisLabel: { show: false },
        splitLine: { show: false },
      },
    ],
    series: [
      {
        name: '价格',
        type: 'line',
        xAxisIndex: 0,
        yAxisIndex: 0,
        data: closes,
        symbol: 'none',
        lineStyle: { width: 1.5, color: isUp ? '#ef4444' : '#22c55e' },
        areaStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: isUp ? 'rgba(239,68,68,0.12)' : 'rgba(34,197,94,0.12)' },
            { offset: 1, color: 'rgba(0,0,0,0)' },
          ]),
        },
      },
      {
        name: '均价',
        type: 'line',
        xAxisIndex: 0,
        yAxisIndex: 0,
        data: avgPrices,
        symbol: 'none',
        lineStyle: { width: 1, color: '#f59e0b', type: 'dashed' as const },
      },
      {
        name: '成交量',
        type: 'bar',
        xAxisIndex: 1,
        yAxisIndex: 1,
        data: volumes,
        itemStyle: {
          color(params: { dataIndex: number }) {
            const idx = params.dataIndex
            if (idx === 0)
              return 'rgba(255,255,255,0.2)'
            return closes[idx] >= closes[idx - 1] ? 'rgba(239,68,68,0.6)' : 'rgba(34,197,94,0.6)'
          },
        },
        barMaxWidth: 3,
      },
    ],
  }
}

async function fetchData() {
  try {
    loading.value = true
    error.value = false
    const res = await ofetch<{ data: MinuteData[] }>(`${props.apiBase}/stock-minute`, {
      query: { code: props.code },
    })
    const data = res.data ?? res as unknown as MinuteData[]
    const option = buildOption(Array.isArray(data) ? data : [])
    if (option && chart.value) {
      chart.value.setOption(option, true)
    }
  }
  catch (err) {
    console.error('Failed to fetch stock minute data:', err)
    error.value = true
  }
  finally {
    loading.value = false
  }
}

onMounted(() => {
  if (!chartRef.value)
    return

  chart.value = echarts.init(chartRef.value)
  fetchData()
  timer = setInterval(fetchData, props.refreshInterval)

  resizeObserver = new ResizeObserver(() => {
    chart.value?.resize()
  })
  resizeObserver.observe(chartRef.value)
})

onUnmounted(() => {
  if (timer)
    clearInterval(timer)
  resizeObserver?.disconnect()
  chart.value?.dispose()
})
</script>

<template>
  <div class="glass-panel flex flex-col p-3">
    <div class="mb-1 flex items-center justify-between">
      <span class="text-xs text-white/60 font-medium">上证指数 · 分时</span>
      <div class="flex items-center gap-2">
        <span v-if="latestPrice" class="text-xs text-white/80 font-mono">{{ latestPrice.toFixed(2) }}</span>
        <span
          class="text-xs font-bold font-mono"
          :class="latestPct >= 0 ? 'text-red-400' : 'text-green-400'"
        >
          {{ latestPct >= 0 ? '+' : '' }}{{ latestPct.toFixed(2) }}%
        </span>
      </div>
    </div>
    <div v-if="error" class="flex flex-1 items-center justify-center text-xs text-white/30">
      数据加载失败
    </div>
    <div v-show="!error" ref="chartRef" class="min-h-0 flex-1" />
  </div>
</template>

<style scoped>
.glass-panel {
  background: rgba(12, 12, 14, 0.45);
  backdrop-filter: blur(20px) saturate(1.2);
  -webkit-backdrop-filter: blur(20px) saturate(1.2);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 16px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.04);
}
</style>
